import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import BottomMenu from "../Buttered_BottomMenu";
import Topmenu from "../Buttered_Topmenu";
import en from "../../../language/Eng_Aust.json";
import { auth } from "../../../firebase";

const Butteredwholesale: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState(false);
  const [loadedImages, setLoadedImages] = useState<{ [key: number]: boolean }>({});

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  const t = en.wholesale;

  useEffect(() => {
    let isMounted = true;

    signOut(auth)
      .catch(() => undefined)
      .finally(() => {
        if (isMounted) {
          setIsLoggedIn(false);
          setIsCheckingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const images = [
    `${process.env.PUBLIC_URL}/assets/Buttered/Buttered_Menu_image/bread1.webp`,
    `${process.env.PUBLIC_URL}/assets/Buttered/Buttered_Menu_image/ButterDDuk.webp`,
    `${process.env.PUBLIC_URL}/assets/Buttered/Buttered_Menu_image/SALTED_BUTTERED_ROLL_Plain.webp`,
    `${process.env.PUBLIC_URL}/assets/Buttered/Buttered_Menu_image/TIRAMISU.webp`,
    `${process.env.PUBLIC_URL}/assets/Buttered/Buttered_Menu_image/cake1.webp`,
  ];

  const handleLogin = async () => {
    if (isLoggingIn) return;

    setIsLoggingIn(true);
    setError(false);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setIsLoggedIn(true);
    } catch {
      setError(true);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  const handleEmailContact = () => {
    window.location.href = "mailto:butteredmanager@butteredsydney.com.au";
  };

  const goToStore = () => {
    window.open("https://butteredwholesale.square.site/", "_blank");
  };

  return (
    <PageWrapper>
      <Topmenu />
      <ContentArea>
        <SuccessContent>
          <PhotoLineContainer>
            <Track>
              {[...images, ...images].map((src, index) => (
                <ImageCard key={index}>
                  {!loadedImages[index] && <Skeleton />}
                  <ImageElement
                    src={src}
                    alt={`Wholesale Presentation ${index}`}
                    onLoad={() => handleImageLoad(index)}
                    $isLoaded={loadedImages[index] || false}
                    loading={index < 3 ? "eager" : "lazy"}
                    {...({ fetchpriority: index === 0 ? "high" : "auto" } as any)}
                  />
                </ImageCard>
              ))}
            </Track>
          </PhotoLineContainer>

          <InfoSection>
            <InfoTitle>{t.title}</InfoTitle>
            <InfoDesc>{t.description}</InfoDesc>

            <BusinessInquiryBox>
              <InquiryTag>IMPORTANT</InquiryTag>
              <InquiryText>{t.businessInquiry}</InquiryText>
            </BusinessInquiryBox>

            <MainActionContainer>
              {!isLoggedIn && !isCheckingAuth ? (
                <LoginBox>
                  <InputGroup>
                    <Input
                      type="email"
                      placeholder={t.idPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyPress}
                      autoComplete="email"
                    />
                    <Input
                      type="password"
                      placeholder={t.pwPlaceholder}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={handleKeyPress}
                      autoComplete="current-password"
                    />
                  </InputGroup>

                  <ButtonGroup>
                    <ActionBtn className="login" onClick={handleLogin} disabled={isLoggingIn}>
                      {isLoggingIn ? "LOGGING IN..." : t.loginBtn}
                    </ActionBtn>
                    <ActionBtn className="email" onClick={handleEmailContact}>{t.btnEmail}</ActionBtn>
                  </ButtonGroup>

                  {error && (
                    <ErrorMessage>
                      {t.loginError} <span onClick={handleEmailContact}>{t.contactGuide}</span>
                    </ErrorMessage>
                  )}
                </LoginBox>
              ) : isLoggedIn ? (
                <LoginBox>
                  <ButtonGroup>
                    <ActionBtn className="wholesale" onClick={goToStore}>{t.btnWholesale}</ActionBtn>
                    <ActionBtn className="email" onClick={handleEmailContact}>{t.btnEmail}</ActionBtn>
                  </ButtonGroup>
                </LoginBox>
              ) : null}
            </MainActionContainer>

            <DeliveryInfoBox>
              <DeliveryTitle>{t.infoTitle}</DeliveryTitle>
              <DeliveryList>
                {t.infoList.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </DeliveryList>
            </DeliveryInfoBox>
          </InfoSection>
        </SuccessContent>
      </ContentArea>
      <BottomMenu />
    </PageWrapper>
  );
};

export default Butteredwholesale;

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #faf8f5;
`;

const ContentArea = styled.main`
  flex: 1;
  width: 100%;
`;

const SuccessContent = styled.div`
  width: 100%;
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const PhotoLineContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: #faf8f5;
  padding: 40px 0;
`;

const Track = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 30s linear infinite;
`;

const ImageCard = styled.div`
  width: 350px;
  height: 450px;
  margin: 0 15px;
  border-radius: 24px;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
`;

const skeletonPulse = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: #e0e0e0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: ${skeletonPulse} 1.5s infinite linear;
  }
`;

const ImageElement = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const InfoSection = styled.div`
  text-align: center;
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const InfoTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 20px;
  color: #333;
  letter-spacing: -0.5px;
`;

const InfoDesc = styled.p`
  color: #777;
  margin-bottom: 40px;
  font-size: 18px;
  line-height: 1.6;
`;

const BusinessInquiryBox = styled.div`
  max-width: 550px;
  margin: 0 auto 50px;
  padding: 30px;
  background-color: #fffbed;
  border: 1px solid #f0df98;
  border-radius: 28px;
  position: relative;
`;

const InquiryTag = styled.span`
  position: absolute;
  top: -12px;
  left: 24px;
  background-color: #5d4037;
  color: white;
  padding: 5px 14px;
  border-radius: 30px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
`;

const InquiryText = styled.p`
  font-size: 15px;
  color: #444;
  line-height: 1.7;
  font-weight: 500;
  word-break: keep-all;
  text-align: left;
  white-space: pre-wrap;
`;

const MainActionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 80px;
`;

const LoginBox = styled.div`
  width: 100%;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 18px 22px;
  border: 1.5px solid #eee;
  border-radius: 16px;
  font-size: 16px;
  outline: none;
  background-color: #fffdf9;
  transition: all 0.2s ease;
  &:focus {
    border-color: #fce444;
    background-color: #fff;
    box-shadow: 0 0 0 4px rgba(252, 228, 68, 0.1);
  }
  &::placeholder {
    color: #bbb;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ActionBtn = styled.button`
  flex: 1;
  padding: 20px;
  border: none;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &.login, &.wholesale {
    background-color: #fce444;
    color: #5d4037;
    box-shadow: 0 4px 12px rgba(252, 228, 68, 0.3);
  }
  
  &.email {
    background-color: #5d4037;
    color: white;
    box-shadow: 0 4px 12px rgba(93, 64, 55, 0.2);
  }
  
  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.05);
    box-shadow: 0 6px 18px rgba(0,0,0,0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 14px;
  margin-top: 5px;
  font-weight: 500;
  span {
    text-decoration: underline;
    cursor: pointer;
    margin-left: 8px;
    color: #5d4037;
  }
`;

const DeliveryInfoBox = styled.div`
  max-width: 550px;
  margin: 0 auto;
  background: #fdfaf0;
  padding: 35px;
  border-radius: 24px;
  text-align: left;
  border: 1px solid #faedc1;
`;

const DeliveryTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 18px;
  color: #5d4037;
`;

const DeliveryList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    font-size: 15px;
    color: #666;
    margin-bottom: 12px;
    line-height: 1.5;
    display: flex;
    align-items: flex-start;
    &:before {
      content: "•";
      color: #fce444;
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: 0;
    }
  }
`;
